import { selectAll } from 'd3-selection';
import { DateTime } from 'luxon';
import { timeZone, createDateTimePrinter } from './utils.mjs';


function ordinalify(num) {
  let suffix = 'th';
  const num10 =  num % 10;
  const num100 = num % 100;
  if (num10 === 1 && num100 !== 11) { suffix = 'st'; }
  else if (num10 === 2 && num100 !== 12) { suffix = 'nd'; }
  else if (num10 === 3 && num100 !== 13) { suffix = 'rd'; }
  return `${num}${suffix}`;
}


// For tokens see: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
const dateFormats = {};
dateFormats.time = createDateTimePrinter('T'); // 13:46
dateFormats.dom = createDateTimePrinter('d'); // 4
dateFormats.odom = ts => ordinalify(dateFormats.dom(ts)) // 4th
dateFormats.dow = createDateTimePrinter('cccc'); // Tuesday
dateFormats.month = createDateTimePrinter('LLLL'); // November
dateFormats.year = createDateTimePrinter('yyyy'); // 2025
dateFormats.date = ts => `${dateFormats.odom(ts)} ${dateFormats.month(ts)}`; // 4th November
dateFormats.ldate = ts => `${dateFormats.dow(ts)} ${dateFormats.date(ts)}`; // Tuesday 4th November
dateFormats.ydate = ts => `${dateFormats.date(ts)} ${dateFormats.year(ts)}`; // 4th November 2025
dateFormats.isoDate = createDateTimePrinter('yyyy-LL-dd'); // 2025-11-04
dateFormats.isoDateTime = createDateTimePrinter('yyyy-LL-dd TZZ'); // 2025-11-04 13:46+00:00


const observer = new MutationObserver(function(mutationList) {
  for (const { target } of mutationList) {
    const timestamp = parseInt(target.dataset.timestamp);
    
    if (target.dataset.date) {
      const f = target.dataset.dateFormat;
      const fmt = dateFormats[f || 'date'];
      if (fmt) {
        target.textContent = fmt(timestamp)
        target.setAttribute('datetime', dateFormats.isoDate(timestamp));
      }
      else { console.warn(`Cannot locate function for format ${f}`); }
    }
    else if (target.dataset.time) {
      target.textContent = dateFormats.time(timestamp);
      target.setAttribute('datetime', dateFormats.isoDateTime(timestamp));
    }
    else if (target.dataset.year) {
      target.textContent = dateFormats.year(timestamp);
      target.setAttribute('datetime', dateFormats.year(timestamp));
    }
  }
});


const timestampLookup = {};

function populateLookup(data) {
  const opt = { zone: timeZone };

  Object.entries(data)
    .map(function([key, d]) {
      const arr = [];
      if (d.dates) {
        for (const [name, date] of Object.entries(d.dates)) {
          const id = `${key}-${name}`;
          const dt = DateTime.fromISO(date, opt).endOf('day');
          arr.push([id, dt.ts]);
        }
      }
      else {
        const date = d.date;
        for (const [name, time] of Object.entries(d.times)) {
          const id = name === "default" ? key : `${key}-${name}`;
          const str = `${date}T${time}`;
          const dt = DateTime.fromISO(str, opt);
          arr.push([id, dt.ts]);
        }
      }

      return arr;
    })
    .flat()
    .forEach(([k, v]) => timestampLookup[k] = v);
}


function addDates(data) {
  populateLookup(data);

  const observerConfig = { subtree: true, attributes: true, attributeFilter: ['data-timestamp']}
  observer.observe(document.body, observerConfig);

  Object.entries(timestampLookup)
    .forEach(function([event, timestamp]) {
      const query = `[data-date="${event}"], [data-time="${event}"], [data-year="${event}"]`;

      selectAll(query)
        .each(function() {
          this.dataset.timestamp = timestamp;
        });
    });
}


export { addDates };
